/**
 * LoadingState — une donnée est en cours de chargement.
 *
 * @remarks
 * Le `Spinner` accompagné d'un libellé qui dit **ce qui** charge
 * (« Chargement des offres… »), pas un « Chargement… » générique.
 *
 * - par défaut, un bloc centré qui remplit sa zone (panneau, vue, page) ;
 * - `inline` : une ligne, pour une section qui charge au milieu d'un contenu
 *   déjà affiché (une carte, une liste sous un titre).
 *
 * Quand la forme du contenu est connue d'avance (une liste, une carte de
 * chiffres), préférer un `Skeleton` à sa silhouette : la mise en page ne saute
 * pas à l'arrivée des données.
 *
 * **A11y** : `role="status"` sur le conteneur, le libellé visible est annoncé ;
 * le `Spinner` reste décoratif.
 */

import { cn } from '@/lib/utils/cn';
import { Spinner } from '@/shared/ui';
import { useIsVeiled } from '@/shared/ui/loader-veil-context';

export interface LoadingStateProps {
  /** Ce qui charge — « Chargement du parcours… ». */
  label: string;
  inline?: boolean;
  className?: string;
}

export function LoadingState({ label, inline = false, className }: LoadingStateProps) {
  // Sous le voile du loader général : même taille, mais invisible (pas de doublon).
  const veiled = useIsVeiled();

  if (inline) {
    return (
      <p
        role="status"
        aria-hidden={veiled || undefined}
        className={cn(
          'text-fg-muted m-0 flex items-center gap-2 text-sm',
          veiled && 'invisible',
          className,
        )}
      >
        <Spinner size="sm" />
        {label}
      </p>
    );
  }

  return (
    <div
      role="status"
      aria-hidden={veiled || undefined}
      className={cn(
        'text-fg-muted flex h-full flex-col items-center justify-center gap-3 px-6 py-8 text-center text-sm',
        veiled && 'invisible',
        className,
      )}
    >
      <Spinner size="md" className="text-primary" />
      <p className="m-0">{label}</p>
    </div>
  );
}
